// Imports React
import React, { useCallback, useContext, useEffect, useState } from "react";
import Select, { components } from 'react-select';
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../../firebase";
// Imports Contexts
import { AppContext } from "../../../contexts/AppContext";
import { AuthContext } from "../../../contexts/AuthContext";
import staticData from '../../../contexts/staticData.json';
import subsidyCodes from '../../../contexts/subsidyCodes.json';
// Imports Helpers
import { formatCurrency } from '../../../shared/helpers';
// Imports Components
import ContainerSearch from "../addPME/ContainerSearch";
import { ConfirmationDialog } from "../salaryExpenses/ConfirmationDialog";
import AddCodesModal from "./AddCodesModal";
import MessageConfirmation from "../../messageConfirmation/MessageConfirmation";
import SubsidyDetailModal from "./SubsidyDetailModal";
// Imports Icons
import arrowSide from '../../../assets/icons/arrowSide.svg';
import downArrow from '../../../assets/icons/downArrow.svg';
import warning from '../../../assets/icons/infoWhite.svg';
import add from '../../../assets/icons/add.svg';
// Imports CSS
import './subsidies.css';

const subsidyNames = {
    general: 'General',
    sep: 'SEP',
    pie: 'PIE',
    eib: 'EIB',
    maintenance: 'Mantenimiento',
    retention: 'Pro Retención',
    internship: 'Internado',
    reinforcement: 'Reforzamiento',
    faep: 'FAEP'
};

const initializeEmptyMonths = () => {
    const emptyMonths = {};
    for (let i = 1; i <= 12; i++) {
        emptyMonths[i] = {};
    }
    return emptyMonths;
};

function IncomeFromSubsidies() {
    const { dataProjects, establishmentList, pmeList } = useContext(AppContext);
    const { userData } = useContext(AuthContext);
    const [dataSearch, setDataSearch] = useState({
        region: null,
        commune: null,
        holder: { value: null, id: null },
        establishment: { value: null, id: null },
    });
    const [pme, setPme] = useState(null);
    const [view, setView] = useState(false);
    const [availablePMEs, setAvailablePMEs] = useState([]);
    const [selectedPMEYear, setSelectedPMEYear] = useState(null);
    const [total, setTotal] = useState(0);
    const [validationError, setValidationError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingExpenseData, setPendingExpenseData] = useState(null);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [selectedCodes, setSelectedCodes] = useState([]);
    const [temporarySelections, setTemporarySelections] = useState([]);
    const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [showSubsidyDetail, setShowSubsidyDetail] = useState(false);
    const [formData, setFormData] = useState({
        periodType: null,
        selectedMonth: null,
        selectedSubsidy: null,
        expenses: {},
        distributedExpenses: {}
    });

    //Helpers
    const getEstablishment = () => {
        const establishmentId = (userData && userData?.dimension === 'Establecimiento')
            ? userData.establishmentId
            : dataSearch.establishment.id;

        return establishmentList.find(establishment => establishment.id === establishmentId);
    };

    const getValidationError = () => {
        if (!formData.periodType) {
            return 'Debe seleccionar un tipo de período';
        }
        if (!formData.selectedSubsidy) {
            return 'Debe seleccionar una subvención';
        }
        if (formData.periodType.value === 'monthly' && !formData.selectedMonth) {
            return 'Debe seleccionar un mes para el período mensual';
        }
        // Validar que la suma de gastos sea mayor a 0
        const expensesSum = Object.values(formData.expenses).reduce((sum, value) => sum + (value || 0), 0);
        if (expensesSum <= 0) {
            return 'El total de gastos debe ser mayor a 0';
        }

        return '';
    };

    const isFormValid = () => {
        return getValidationError() === '';
    };

    const resetFormData = () => {
        setFormData({
            periodType: null,
            selectedMonth: null,
            selectedSubsidy: null,
            expenses: {},
            distributedExpenses: {}
        });
        setTotal(0);
        setValidationError('');
        setSelectedCodes([]);
        setTemporarySelections([]);
        setShowSearchModal(false);
    };

    const handleMessageComplete = () => {
        setShowConfirmationMessage(false);
        setShowErrorMessage(false);
    };
    //Effects y callback
    const updatePMEData = useCallback(() => {
        if (!pmeList || !establishmentList) return;

        const currentYear = new Date().getFullYear();
        const establishmentId = userData?.dimension === 'Establecimiento'
            ? userData?.establishmentId
            : dataSearch?.establishment?.id;

        if (establishmentId) {
            if (userData?.permissions === 'admin' || userData?.permissions === 'admin-holder') {
                const establishmentPMEs = pmeList.filter(pme =>
                    pme.establishmentId === establishmentId
                );

                setAvailablePMEs(establishmentPMEs);

                // Si hay PMEs disponibles para este establecimiento
                if (establishmentPMEs.length > 0) {
                    if (!selectedPMEYear) {
                        // Intentar encontrar el PME del año actual
                        const currentPME = establishmentPMEs.find(pme => pme.yearPME === currentYear);

                        if (currentPME) {
                            // Si existe PME del año actual, usarlo
                            setPme(currentPME);
                            setSelectedPMEYear({
                                value: currentPME.yearPME,
                                label: `PME ${currentPME.yearPME}`
                            });
                        } else {
                            // Si no existe PME del año actual, usar el más reciente
                            const latestPME = establishmentPMEs.reduce((latest, current) =>
                                current.yearPME > latest.yearPME ? current : latest
                            );
                            setPme(latestPME);
                            setSelectedPMEYear({
                                value: latestPME.yearPME,
                                label: `PME ${latestPME.yearPME}`
                            });
                        }
                        setView(true);
                    }
                    return;
                }

                // Si no hay PMEs, limpiar estados
                setPme(null);
                setSelectedPMEYear(null);
                setView(false);
                setSelectedCodes([]);
                setTemporarySelections([]);
                setShowSearchModal(false);
                return;
            }

            // Para usuarios no admin
            const updatedPME = pmeList.find(pme =>
                pme.establishmentId === establishmentId &&
                pme.yearPME === currentYear
            );

            if (updatedPME) {
                setPme(updatedPME);
                setView(true);
            } else {
                setPme(null);
                setView(false);
            }
        }
    }, [pmeList, establishmentList, userData, dataSearch?.establishment?.id, selectedPMEYear]);

    useEffect(() => {
        if (userData) {
            updatePMEData();
        }
    }, [updatePMEData, userData]);

    useEffect(() => {
        const error = getValidationError();
        setValidationError(error);
    }, [formData.periodType, formData.selectedMonth, formData.selectedSubsidy, formData.expenses]);

    useEffect(() => {
        const sum = Object.values(formData.expenses).reduce((acc, curr) => acc + (curr || 0), 0);
        setTotal(sum);
    }, [formData.expenses]);

    // Events Handlers
    const handleSelect = (selectedOption, { name }) => {
        const value = selectedOption ? selectedOption.value : null;
        const id = selectedOption ? selectedOption.id : null;
        if (name === 'establishment') {
            setPme(null);
            setDataSearch(prevState => ({
                ...prevState,
                establishment: { value, id }
            }));
            // Si hay un nuevo establecimiento seleccionado, actualizar datos
            if (id) {
                setTimeout(() => {
                    updatePMEData();
                    setSelectedPMEYear(null);
                }, 0);
            } else {
                setView(false);
            }
        } else {
            setDataSearch(prevState => ({
                ...prevState,
                [name]: name === 'holder' || name === 'establishment'
                    ? { value, id }
                    : value,
                ...(name === 'region' && { commune: null, holder: { value: null, id: null }, establishment: null }),
                ...(name === 'commune' && { holder: { value: null, id: null }, establishment: null }),
                ...(name === 'holder' && { establishment: null })
            }));
            setView(false);
        }
    };
    const handleButtonClick = () => {
        updatePMEData();
    };

    const handlePMEChange = (option) => {
        setSelectedPMEYear(option);
        resetFormData();

        if (option) {
            // Buscar el PME correspondiente al año seleccionado
            const selectedPME = availablePMEs.find(pme => pme.yearPME === option.value);
            if (selectedPME) {
                setPme(selectedPME);
                setView(true);
            }
        }
    };

    const handleSelectChange = (selectedOption, { name }) => {
        setFormData(prev => {
            const updates = {
                ...prev,
                [name]: selectedOption
            };

            // Reseteos adicionales según el campo que cambia
            if (name === 'selectedSubsidy') {
                updates.expenses = {};
            } else if (name === 'periodType') {
                updates.selectedMonth = null;
                updates.distributedExpenses = {};
            }

            return updates;
        });
    };

    const handleExpenseChange = (code, value) => {
        const cleanValue = value.replace(/[$\s.]/g, '');
        const numericValue = cleanValue === '' ? 0 : parseInt(cleanValue, 10);

        if (!isNaN(numericValue)) {
            setFormData(prev => ({
                ...prev,
                expenses: {
                    ...prev.expenses,
                    [code]: numericValue
                }
            }));
        }
    };

    //Save Data
    const updatePMEFirestore = async (pmeId, incomeFromSubsidies) => {
        try {
            const pmeDocRef = doc(db, 'pme', pmeId);

            //Actualizar salaryExpense y Activities
            await updateDoc(pmeDocRef, {
                incomeFromSubsidies: incomeFromSubsidies
            });
            return true;
        } catch (error) {
            console.error('Error al actualizar PME en firestore:', error);
            throw error;
        }
    };

    const saveChanges = async (isMerging, resolutionType = 'sum') => {
        try {
            let newIncomeSubsidies = { ...(pme.incomeFromSubsidies || {}) };
            const subsidyType = formData.selectedSubsidy.value;

            // Inicializar la subvención si no existe
            if (!newIncomeSubsidies[subsidyType]) {
                newIncomeSubsidies[subsidyType] = initializeEmptyMonths();
            }

            // Solo guardar códigos con montos mayores a 0
            Object.entries(formData.expenses).forEach(([code, amount]) => {
                if (amount > 0) {
                    switch (formData.periodType.value) {
                        case 'monthly':
                            // Si existe el código y hay conflicto
                            if (isMerging) {
                                const month = formData.selectedMonth.value;
                                if (resolutionType === 'sum') {
                                    newIncomeSubsidies[subsidyType][month][code] =
                                        (newIncomeSubsidies[subsidyType][month][code] || 0) + amount;
                                } else {
                                    newIncomeSubsidies[subsidyType][month][code] = amount;
                                }
                            } else {
                                newIncomeSubsidies[subsidyType][formData.selectedMonth.value][code] = amount;
                            }
                            break;

                        case 'accumulated':
                            const currentMonth = new Date().getMonth() + 1;
                            const monthlyAmount = Math.floor(amount / currentMonth);
                            const remainder = amount % currentMonth;

                            for (let month = 1; month <= currentMonth; month++) {
                                if (isMerging) {
                                    if (resolutionType === 'sum') {
                                        newIncomeSubsidies[subsidyType][month][code] =
                                            (newIncomeSubsidies[subsidyType][month][code] || 0) +
                                            (monthlyAmount + (month === 1 ? remainder : 0));
                                    } else {
                                        newIncomeSubsidies[subsidyType][month][code] =
                                            monthlyAmount + (month === 1 ? remainder : 0);
                                    }
                                } else {
                                    newIncomeSubsidies[subsidyType][month][code] =
                                        monthlyAmount + (month === 1 ? remainder : 0);
                                }
                            }
                            break;

                        case 'annual':
                            const annualMonthlyAmount = Math.floor(amount / 12);
                            const annualRemainder = amount % 12;

                            for (let month = 1; month <= 12; month++) {
                                if (isMerging) {
                                    if (resolutionType === 'sum') {
                                        newIncomeSubsidies[subsidyType][month][code] =
                                            (newIncomeSubsidies[subsidyType][month][code] || 0) +
                                            (annualMonthlyAmount + (month === 1 ? annualRemainder : 0));
                                    } else {
                                        newIncomeSubsidies[subsidyType][month][code] =
                                            annualMonthlyAmount + (month === 1 ? annualRemainder : 0);
                                    }
                                } else {
                                    newIncomeSubsidies[subsidyType][month][code] =
                                        annualMonthlyAmount + (month === 1 ? annualRemainder : 0);
                                }
                            }
                            break;
                    }
                }
            });

            const updatedPme = {
                ...pme,
                incomeFromSubsidies: newIncomeSubsidies
            };
            setPme(updatedPme);
            return updatedPme;
        } catch (error) {
            console.error('Error al guardar los cambios:', error);
            throw error;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormValid()) { return }

        try {
            const monthlyDistribution = calculateMonthlyDistribution();

            const hasConflicts = checkForConflicts(monthlyDistribution);
            if (hasConflicts) {
                const resolution = await handleConflictResolution(monthlyDistribution);
                if (resolution === "cancel") return;

                setIsLoading(true);
                try {
                    const updatedPME = await saveChanges(monthlyDistribution, true, resolution);
                    if (updatedPME) {
                        //Guardar en firestore
                        await updatePMEFirestore(
                            updatedPME.id,
                            updatedPME.incomeFromSubsidies
                        );
                        setShowConfirmationMessage(true);
                        resetFormData();
                    }
                } catch (error) {
                    setShowErrorMessage(true);
                    console.error("Error al guardar los datos:", error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(true);
                try {
                    const updatedPME = await saveChanges(monthlyDistribution, false);
                    if (updatedPME) {
                        // Guardar en Firestore
                        await updatePMEFirestore(
                            updatedPME.id,
                            updatedPME.incomeFromSubsidies
                        );
                        setShowConfirmationMessage(true);
                        resetFormData();
                    }
                } catch (error) {
                    setShowErrorMessage(true);
                    console.error("Error al guardar los datos:", error);
                } finally {
                    setIsLoading(false);
                }
            }
        } catch (error) {
            setShowErrorMessage(true);
            console.error("Error al guardar los datos:", error);
        }
    };

    //Funciones de conflictos
    const calculateMonthlyDistribution = () => {
        const distribution = initializeEmptyMonths();

        switch (formData.periodType.value) {
            case 'monthly': {
                distribution[formData.selectedMonth.value] = { ...formData.expenses };
                break;
            }
            case 'accumulated': {
                const currentMonth = new Date().getMonth() + 1;

                // Encontrar el primer mes vacío
                let startMonth = 1;
                for (let month = 1; month <= currentMonth; month++) {
                    if (!pme.incomeFromSubsidies?.[month] || Object.keys(pme.incomeFromSubsidies[month]).length === 0) {
                        startMonth = month;
                        break;
                    }
                }

                // Calcular cantidad de meses para la distribución
                const monthCount = currentMonth - startMonth + 1;

                if (monthCount > 0) {
                    // Distribuir cada código entre los meses
                    Object.entries(formData.expenses).forEach(([code, amount]) => {
                        const monthlyAmount = Math.floor(amount / monthCount);
                        const remainder = amount % monthCount;

                        for (let month = startMonth; month <= currentMonth; month++) {
                            distribution[month][code] = monthlyAmount + (month === startMonth ? remainder : 0);
                        }
                    });
                }
                break;
            }
            case 'annual': {
                // Distribuir entre los 12 meses
                Object.entries(formData.expenses).forEach(([code, amount]) => {
                    const monthlyAmount = Math.floor(amount / 12);
                    const remainder = amount % 12;

                    for (let month = 1; month <= 12; month++) {
                        if (!distribution[month][code]) {
                            distribution[month][code] = 0;
                        }
                        distribution[month][code] = monthlyAmount + (month === 1 ? remainder : 0);
                    }
                });
                break;
            }
        }

        return distribution;
    };

    const checkForConflicts = () => {
        if (!pme.incomeFromSubsidies?.[formData.selectedSubsidy.value]) return false;

        // Verificar si algún código ya tiene monto asignado
        return Object.entries(formData.expenses)
            .some(([code, amount]) => {
                if (amount <= 0) return false;

                switch (formData.periodType.value) {
                    case 'monthly':
                        return !!pme.incomeFromSubsidies[formData.selectedSubsidy.value]
                        [formData.selectedMonth.value]?.[code];

                    case 'accumulated':
                    case 'annual':
                        const months = Object.values(pme.incomeFromSubsidies[formData.selectedSubsidy.value]);
                        return months.some(month => !!month[code]);
                }
            });
    };

    const handleConflictResolution = async (monthlyDistribution) => {
        return new Promise((resolve) => {
            const relevantMonths = Object.keys(monthlyDistribution).filter(
                month => Object.keys(monthlyDistribution[month]).length > 0
            );

            const existingData = relevantMonths.reduce((acc, month) => {
                const subsidyType = formData.selectedSubsidy.value;
                if (pme.incomeFromSubsidies?.[subsidyType]?.[month]) {
                    acc[month] = pme.incomeFromSubsidies[subsidyType][month];
                }
                return acc;
            }, {});

            setPendingExpenseData({
                existingData,
                newData: monthlyDistribution,
                onSum: async () => {
                    setShowConfirmDialog(false);
                    resolve("sum");
                },
                onOverwrite: async () => {
                    setShowConfirmDialog(false);
                    resolve("overwrite");
                },
                onCancel: () => {
                    setShowConfirmDialog(false);
                    resolve("cancel");
                }
            });
            setShowConfirmDialog(true);
        });
    };

    return (
        <section className='income-subsidies flex'>
            <ContainerSearch
                dataSearch={dataSearch}
                setDataSearch={setDataSearch}
                handleSelect={handleSelect}
                handleButtonClick={handleButtonClick}
                dataProjects={dataProjects}
                establishmentList={establishmentList}
            />
            <section className='box-form-subsidies flex'>
                {(view && pme)
                    ? (
                        <>
                            {((dataSearch?.establishment?.value || userData?.establishmentId) && getEstablishment()) && (
                                <article className='info-establishment flex'>
                                    <span className='flex'>
                                        <h3>{getEstablishment().nameEstablishment}</h3>
                                        <img src={arrowSide} className='icon' alt='Icono de flecha' />
                                        <p>{`Ingreso de Subvenciones año ${pme?.yearPME}`}</p>
                                    </span>
                                    {(userData?.permissions === 'admin' || userData?.permissions === 'admin-holder') && (
                                        <Select
                                            options={availablePMEs
                                                .sort((a, b) => b.yearPME - a.yearPME) // Ordenar de mayor a menor año
                                                .slice(0, userData?.permissions === 'admin-holder' ? 2 : undefined) // Tomar solo los 2 primeros si es admin-holder
                                                .map(pme => ({
                                                    value: pme.yearPME,
                                                    label: `PME ${pme.yearPME}`
                                                }))}
                                            value={selectedPMEYear}
                                            onChange={handlePMEChange}
                                            placeholder="Seleccionar Año PME"
                                            className="custom-select"
                                            classNamePrefix="custom-select"
                                            components={{ DropdownIndicator }}
                                            isClearable
                                        />
                                    )}
                                </article>
                            )}
                            <form className='form-subsidies-expenses flex'>
                                <h3>Formulario Ingresos por Subvenciones</h3>
                                <span className='input-text flex'>
                                    <label htmlFor='establishment'>Establecimiento:</label>
                                    <input type='text' className='input flex' value={getEstablishment()?.nameEstablishment} readOnly />
                                </span>
                                <span className='select flex'>
                                    <label htmlFor='selectedSubsidy'>Subvención:</label>
                                    <Select
                                        name='selectedSubsidy'
                                        options={Object.keys(subsidyNames).map((key) => ({
                                            value: key,
                                            label: subsidyNames[key],
                                        }))}
                                        value={formData.selectedSubsidy}
                                        onChange={handleSelectChange}
                                        placeholder='Seleccione una subvención'
                                        className="custom-select"
                                        classNamePrefix="custom-select"
                                        components={{ DropdownIndicator }}
                                    />
                                </span>
                                <span className='select flex'>
                                    <label>Tipo de Período:</label>
                                    <Select
                                        name='periodType'
                                        options={staticData.periodOptions}
                                        value={formData.periodType}
                                        onChange={handleSelectChange}
                                        placeholder='Seleccione tipo de período'
                                        className='custom-select'
                                        classNamePrefix='custom-select'
                                        components={{ DropdownIndicator }}
                                    />
                                </span>
                                {formData.periodType?.value === 'monthly' && (
                                    <span className="select flex">
                                        <label>Mes:</label>
                                        <Select
                                            name='selectedMonth'
                                            options={staticData.monthOptions}
                                            value={formData.selectedMonth}
                                            onChange={handleSelectChange}
                                            placeholder="Seleccione mes"
                                            className="custom-select"
                                            classNamePrefix="custom-select"
                                            components={{ DropdownIndicator }}
                                        />
                                    </span>
                                )}
                                {formData.periodType && (
                                    <article className='expense-categories flex'>
                                        <span className='flex'>
                                            <p>Cuenta SUPEREDUC</p>
                                            <button
                                                type='button'
                                                onClick={() => setShowSearchModal(true)}
                                                className='add-category-button flex'
                                                disabled={!formData.selectedSubsidy}
                                            >
                                                <img src={add} className='icon' alt='Icono de agregar nueva categoría' />
                                                <p>Agregar Nueva Categoría</p>
                                            </button>
                                        </span>
                                        <table className='expense-table'>
                                            <thead>
                                                <tr>
                                                    <th>Código</th>
                                                    <th>Subcategoría de Ingresos</th>
                                                    <th>Monto</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedCodes.length > 0 ? (
                                                    selectedCodes
                                                        .map((item) => (
                                                            <tr key={item.code}>
                                                                <td className='code-cell'>{item.code}</td>
                                                                <td className='name-cell'>{item.name}</td>
                                                                <td className='amount-cell'>
                                                                    <input
                                                                        type='text'
                                                                        value={formData.expenses[item.code] ? formatCurrency(formData.expenses[item.code]) : ''}
                                                                        onChange={(e) => handleExpenseChange(item.code, e.target.value)}
                                                                        placeholder='$ 0'
                                                                        className='amount-input'
                                                                    />
                                                                </td>
                                                            </tr>
                                                        ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan='3'>No hay subcategorías agregadas aún.</td>
                                                    </tr>
                                                )
                                                }
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan='2' className='total-label'>Total Ingresos:</td>
                                                    <td className='total-amount'>{formatCurrency(total)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </article>
                                )}
                                <article className='button-container'>
                                    <button
                                        className={`sent-form flex ${isFormValid() ? 'disabled' : ''}`}
                                        disabled={!isFormValid()}
                                        onClick={handleSubmit}
                                    >
                                        <p>Guardar</p>
                                    </button>
                                    {validationError && (
                                        <span className='tooltip-text flex'>
                                            <img src={warning} alt='Icono de Advertencia' />
                                            <p>{validationError}</p>
                                        </span>
                                    )}
                                </article>
                                <button
                                    className='button-show-subsidies flex'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowSubsidyDetail(true);
                                    }}
                                >
                                    <p>Ver Ingresos por Subvenciones PME {pme?.yearPME}</p>
                                </button>
                            </form>
                        </>
                    )
                    : (<p className='text-bold message-not-found flex'>No existe PME activo para este establecimiento</p>)
                }
                <AddCodesModal
                    showModal={showSearchModal}
                    setShowModal={setShowSearchModal}
                    temporarySelections={temporarySelections}
                    setTemporarySelections={setTemporarySelections}
                    selectedCodes={selectedCodes}
                    setSelectedCodes={setSelectedCodes}
                    subsidyCodes={subsidyCodes}
                    selectedSubsidy={formData.selectedSubsidy}
                />
                {showConfirmDialog && (
                    <ConfirmationDialog
                        existingData={pendingExpenseData.existingData}
                        newData={pendingExpenseData.newData}
                        onSum={pendingExpenseData.onSum}
                        onOverwrite={pendingExpenseData.onOverwrite}
                        onCancel={pendingExpenseData.onCancel}
                    />
                )}
                {showSubsidyDetail && (
                    <SubsidyDetailModal
                        pme={pme}
                        onClose={setShowSubsidyDetail}
                        subsidyCodes={subsidyCodes}
                    />
                )}
            </section>
            <MessageConfirmation
                confirmation={showConfirmationMessage}
                error={showErrorMessage}
                view={false}
                onComplete={handleMessageComplete}
            />
        </section>
    )
};

const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <img src={downArrow} alt='CustomArrow' />
        </components.DropdownIndicator>
    );
};

export default IncomeFromSubsidies